import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Trophy, Sparkles, Bookmark, HeartHandshake } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import PayPalCheckoutButton from "../components/PayPalCheckoutButton";

const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

export default function Leaderboard() {
  const { user } = useAuth();
  const [board, setBoard] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, mine] = await Promise.all([
          api.get("/leaderboard"),
          user ? api.get("/leaderboard/me").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        setBoard(data);
        setMe(mine.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="leaderboard-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" strokeWidth={2} /> Community
          </div>
          <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter">
            The Contributors<br /><span className="italic font-normal">Board.</span>
          </h1>
          <p className="text-black/60 mt-4 max-w-xl">
            The sharpest eyes on the internet. Every rank is earned by shipping approved deals that our editors greenlit.
          </p>
        </div>
        <Link to="/submit" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] shrink-0" data-testid="board-submit-cta">
          <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Submit a deal
        </Link>
      </div>

      {user && me && (
        <div className="border-2 border-black bg-white p-5 mb-8 flex items-center gap-4" data-testid="my-rank-card">
          <div className="w-14 h-14 bg-black text-white flex items-center justify-center font-serif font-black text-xl shrink-0">
            {me.rank ? `#${me.rank}` : "—"}
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-1">Your standing</div>
            <div className="font-serif font-bold text-xl">{user.name}</div>
            <div className="text-xs text-black/60 font-mono mt-1">
              {me.approved_count} approved · {me.pending_count} pending · {me.rejected_count} not accepted
            </div>
          </div>
          {!me.rank && (
            <Link to="/submit" className="text-xs font-bold uppercase tracking-widest underline hover:text-[#E63946] shrink-0">
              Submit your first →
            </Link>
          )}
        </div>
      )}

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-widest text-black/40">Loading contributors…</div>
      ) : board.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center" data-testid="board-empty">
          <div className="font-serif text-3xl mb-2">No contributors yet</div>
          <p className="text-black/60 text-sm mb-4">Be the first name on the board.</p>
          <Link to="/submit" className="inline-block bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]">
            Submit a deal
          </Link>
        </div>
      ) : (
        <div className="border border-black/10 bg-white divide-y divide-black/10" data-testid="board-list">
          {board.map((entry) => {
            const isMe = user && entry.submitter_id === user.id;
            const medal = MEDAL[entry.rank];
            return (
              <div
                key={entry.submitter_id || entry.submitter_name}
                className={`flex items-center gap-4 p-5 ${isMe ? "bg-[#FDECEE]" : ""}`}
                data-testid={`board-row-${entry.rank}`}
              >
                <div className="w-12 shrink-0 font-serif font-black text-2xl leading-none">
                  {medal || <span className="text-black/50">#{entry.rank}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif font-bold text-xl truncate" data-testid={`board-name-${entry.rank}`}>
                    {entry.submitter_name}
                    {isMe && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-[#E63946]">You</span>}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-black/50 mt-1">
                    {entry.approved_count} approved
                    {entry.featured_count > 0 && ` · ${entry.featured_count} featured`}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-black/60 shrink-0" title="Bookmarks earned across their deals">
                  <Bookmark className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {entry.total_bookmarks}
                </div>
                <div className="font-mono text-xl font-black tabular-nums w-10 text-right shrink-0" data-testid={`board-count-${entry.rank}`}>
                  {entry.approved_count}
                </div>
                {!isMe && (
                  <TipButton contributorId={entry.submitter_id} contributorName={entry.submitter_name} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-black/40 font-mono uppercase tracking-widest mt-8 text-center">
        Rankings update in real time · Featured deals count double towards perks
      </p>
    </div>
  );
}

function TipButton({ contributorId, contributorName }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(3);
  const [loading, setLoading] = useState(false);

  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        tip_amount_cents: Math.round(amount * 100),
        tip_contributor_id: contributorId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 border border-black/20 hover:border-black px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white"
        data-testid={`tip-btn-${contributorId}`}
        title={`Tip ${contributorName}`}
      >
        <HeartHandshake className="w-3 h-3" strokeWidth={2} /> Tip
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 border-2 border-black bg-white z-20 p-3 shadow-lg" data-testid={`tip-panel-${contributorId}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#E63946] mb-2">Tip {contributorName.split(" ")[0]}</div>
          <div className="grid grid-cols-4 gap-1 mb-2">
            {[1, 3, 5, 10].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(v)}
                className={`text-xs font-bold py-1.5 border ${amount === v ? "bg-black text-white border-black" : "border-black/20"}`}
                data-testid={`tip-preset-${v}`}
              >${v}</button>
            ))}
          </div>
          <input
            type="number"
            min={1} max={20} step={1}
            value={amount}
            onChange={(e) => setAmount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            className="w-full border border-black px-2 py-1.5 text-sm font-mono mb-2"
            data-testid={`tip-amount-${contributorId}`}
          />
          <button
            onClick={send}
            disabled={loading}
            className="w-full bg-black text-white py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50"
            data-testid={`tip-submit-${contributorId}`}
          >
            {loading ? "…" : `Send $${amount} (Card)`}
          </button>
          <PayPalCheckoutButton
            payload={{ tip_amount_cents: Math.round(amount * 100), tip_contributor_id: contributorId }}
            onPaid={() => { setOpen(false); toast.success(`Tipped ${contributorName.split(" ")[0]} $${amount} via PayPal ✓`); }}
            testIdPrefix={`paypal-tip-${contributorId}`}
          />
        </div>
      )}
    </div>
  );
}
